import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";
import { safeGet, safeSet, safeRemove } from "./safeStorage";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // If returning from OAuth callback, skip /me check — AuthCallback will exchange first
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = safeGet("nexus_token");
    // No token? Still try /me — cookie-based Google session might be active
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setProfile(data.profile);
    } catch (e) {
      if (token) safeRemove("nexus_token");
      setUser(null); setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    safeSet("nexus_token", data.token);
    await refresh();
    return data.user;
  };

  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    safeSet("nexus_token", data.token);
    await refresh();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    safeRemove("nexus_token");
    setUser(null); setProfile(null);
  };

  return (
    <AuthCtx.Provider value={{ user, profile, loading, login, signup, logout, refresh, setProfile, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
